import { getErrorMessage } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { getRememberedEmail, getTrustDevicePreference, setTrustDevicePreference } from '@/lib/device';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Alert } from '@/components/ui/Alert';
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [trustDevice, setTrustDevice] = useState(getTrustDevicePreference);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const passwordReset = searchParams.get('senha') === 'redefinida';
  const forgotHref = email.trim()
    ? `/esqueci-senha?email=${encodeURIComponent(email.trim())}`
    : '/esqueci-senha';

  useEffect(() => {
    const remembered = getRememberedEmail();
    if (remembered) setEmail(remembered);
  }, []);

  if (!loading && user) return <Navigate to="/" replace />;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      setTrustDevicePreference(trustDevice);
      await login(email, password, { trustDevice });
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível entrar.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      <div
        className="relative hidden lg:flex lg:w-1/2 flex-col justify-between overflow-hidden p-12 text-white"
        style={{
          backgroundImage: "url('/images/login-hero.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-manejai-forest/85 via-manejai-forest/55 to-manejai-moss/70" />
        <div className="relative z-10">
          <p className="font-display text-3xl font-semibold">Manejai</p>
          <p className="mt-2 text-manejai-cream/90 text-lg">Gestão de rebanho com clareza</p>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          <p className="text-manejai-cream/90 text-sm leading-relaxed">
            Cadastre propriedades, acompanhe eventos reprodutivos, registre pesagens e
            movimentações — tudo sincronizado com seu rebanho.
          </p>
          <div className="flex gap-3 text-xs text-manejai-cream/80">
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1">Reprodução</span>
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1">Pesagens</span>
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1">Movimentações</span>
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <p className="font-display text-2xl font-semibold text-manejai-forest">Manejai</p>
            <p className="text-sm text-manejai-bark/70 mt-1">Entre na sua conta</p>
          </div>

          <h1 className="hidden lg:block font-display text-2xl font-semibold text-manejai-forest mb-2">
            Bem-vindo de volta
          </h1>
          <p className="hidden lg:block text-sm text-manejai-bark/70 mb-8">
            Acesse o painel da sua propriedade
          </p>

          {passwordReset && !error && (
            <Alert variant="success" className="mb-4">
              Senha redefinida. Entre com a nova senha.
            </Alert>
          )}

          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="E-mail"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="Senha"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="flex justify-end -mt-2">
              <Link to={forgotHref} className="text-sm font-medium text-manejai-moss hover:underline">
                Esqueci minha senha
              </Link>
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-manejai-sand text-manejai-forest focus:ring-manejai-sage"
              />
              <span className="text-sm text-manejai-bark/75 leading-snug">
                Confiar neste dispositivo e manter conectado
              </span>
            </label>
            <Button type="submit" className="w-full" size="lg" loading={submitting}>
              Entrar
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-manejai-bark/70">
            Primeira vez?{' '}
            <Link to="/cadastro" className="font-medium text-manejai-moss hover:underline">
              Criar conta
            </Link>
          </p>
          <p className="mt-4 text-center text-xs text-manejai-bark/55">
            <Link to="/termos" className="hover:underline" target="_blank" rel="noopener noreferrer">
              Termos de Uso
            </Link>
            {' · '}
            <Link to="/privacidade" className="hover:underline" target="_blank" rel="noopener noreferrer">
              Política de Privacidade
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
